import React, { useState, useEffect, useRef } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { X, Clock, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { extractCustomerInfo } from '../utils/orderHelpers';

interface ManualOrderModalProps {
  isOpen: boolean;
  order: any;
  onAccept: (deliveryTime: number) => Promise<void>;
  onReject: () => Promise<void>;
  onClose: () => void;
}

const ManualOrderModal: React.FC<ManualOrderModalProps> = ({
  isOpen,
  order,
  onAccept,
  onReject,
  onClose
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const [selectedTime, setSelectedTime] = useState<number>(30);
  const [customTime, setCustomTime] = useState<string>('');
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const presetTimes = [15, 20, 30, 45, 60, 90];

  // Sound alert and countdown
  useEffect(() => {
    if (isOpen && order) {
      playAlertSound();
      setCountdown(60);
      setSelectedTime(30);
      setCustomTime('');
      setUseCustomTime(false);
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            handleAutoReject();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);

  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      const playBeep = (frequency: number, duration: number, delay: number = 0) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + duration);
        }, delay);
      };

      playBeep(800, 0.2, 0);
      playBeep(600, 0.2, 300);
      playBeep(800, 0.2, 600);
      playBeep(600, 0.2, 900);
      playBeep(1000, 0.3, 1200);
    } catch (error) {
      // Audio not available
    }
  };

  const handleAutoReject = async () => {
    if (loading) return;
    try {
      setLoading(true);
      await onReject();
      onClose();
    } catch (error) {
      console.error('Auto-reject failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const finalTime = useCustomTime ? parseInt(customTime) : selectedTime;
      if (isNaN(finalTime) || finalTime < 5 || finalTime > 180) {
        alert(t('manualAcceptance.invalidTimeAlert'));
        return;
      }
      await onAccept(finalTime);
      onClose();
    } catch (error) {
      console.error('Error accepting order:', error);
      alert(t('acceptFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await onReject();
      onClose();
    } catch (error) {
      console.error('Error rejecting order:', error);
      alert(t('rejectFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePresetTimeClick = (time: number) => {
    setSelectedTime(time);
    setUseCustomTime(false);
    setCustomTime('');
  };

  const handleCustomTimeChange = (value: string) => {
    setCustomTime(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setSelectedTime(numValue);
      setUseCustomTime(true);
    }
  };

  if (!isOpen || !order) return null;

  const info = extractCustomerInfo(order);
  const orderTotal = Number(order.total_amount || 0);
  const isUrgent = countdown <= 15;

  return (
    <div className="cashier-overlay fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className={`cashier-dialog bg-surface-1 rounded-xl shadow-2xl border-2 w-full max-w-lg max-h-[90vh] overflow-y-auto transition-colors ${
        isUrgent ? 'border-red-500 shadow-red-500/30' : 'border-amber-500 shadow-amber-500/20'
      }`}>

        {/* Header with countdown */}
        <div className={`cashier-dialog-header px-5 py-4 border-b transition-colors ${
          isUrgent ? 'bg-red-600 border-red-500' : 'bg-amber-600 border-amber-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-t1 animate-pulse" />
              <div>
                <h2 className="text-lg font-bold text-t1">{t('manualAcceptance.title')}</h2>
                <p className="text-sm text-t1/80">
                  #{order.short_code || order.platform_order_id} &middot; {order.platform_name?.toUpperCase()}
                </p>
              </div>
            </div>
            
            {/* Countdown */}
            <div className={`flex flex-col items-center px-4 py-2 rounded-lg ${
              isUrgent ? 'bg-white text-red-600' : 'bg-white/20 text-t1'
            }`}>
              <span className={`text-2xl font-black tabular-nums ${isUrgent ? 'animate-pulse' : ''}`}>
                {countdown}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider">{t('manualAcceptance.seconds')}</span>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Order Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-3 border border-line-strong">
              <p className="text-xs text-t3 mb-0.5">{t('manualAcceptance.customer')}</p>
              <p className="text-sm font-semibold text-t1 truncate">{info.name}</p>
            </div>
            <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-3 border border-line-strong">
              <p className="text-xs text-t3 mb-0.5">{t('manualAcceptance.total')}</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatMoney(orderTotal)}</p>
            </div>
            {info.phone && (
              <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-3 border border-line-strong">
                <p className="text-xs text-t3 mb-0.5">{t('manualAcceptance.phone')}</p>
                <p className="text-sm text-t2">{info.phone}</p>
              </div>
            )}
            <div className="bg-surface-2 dark:bg-surface-2/50 rounded-lg p-3 border border-line-strong">
              <p className="text-xs text-t3 mb-0.5">{t('manualAcceptance.payment')}</p>
              <p className="text-sm text-t2 capitalize">{order.payment_type || 'Cash'}</p>
            </div>
          </div>

          {/* Delivery Time Selection */}
          <div>
            <label className="block text-xs font-semibold text-t3 uppercase tracking-wider mb-2">
              {t('manualAcceptance.deliveryTime')}
            </label>
            <div className="grid grid-cols-3 gap-1.5 mb-2">
              {presetTimes.map((time) => (
                <button
                  key={time}
                  onClick={() => handlePresetTimeClick(time)}
                  disabled={loading}
                  className={`py-2.5 rounded-lg text-sm font-bold transition-all ${
                    selectedTime === time && !useCustomTime
                      ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                      : 'bg-surface-2 text-t2 border border-line-strong hover:border-blue-500 hover:text-blue-700 dark:hover:text-blue-300'
                  }`}
                >
                  {time} min
                </button>
              ))}
            </div>

            {/* Custom time */}
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={customTime}
                onChange={(e) => handleCustomTimeChange(e.target.value)}
                placeholder={t('manualAcceptance.customTimePlaceholder')}
                min="5"
                max="180"
                disabled={loading}
                className="flex-1 px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-sm text-t1 placeholder-t3 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-t3 whitespace-nowrap">min (5-180)</span>
            </div>
          </div>

          {/* Urgency warning */}
          {isUrgent && (
            <div className="flex items-center gap-2 p-3 bg-red-500/15 border border-red-500/30 rounded-lg animate-pulse">
              <Clock className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-semibold text-red-700 dark:text-red-300">
                {t('manualAcceptance.autoRejectWarning', { countdown })}
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="px-5 py-4 border-t border-line flex gap-3">
          <button
            onClick={handleReject}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            {t('common:reject')}
          </button>
          <button
            onClick={handleAccept}
            disabled={loading || (useCustomTime && (!customTime || parseInt(customTime) < 5))}
            className="flex-[2] flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {t('manualAcceptance.acceptWithTime', { time: useCustomTime ? customTime : selectedTime })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualOrderModal;

